/**
 * Wrapper around generated RTK Query API with cleaner naming and custom logic.
 *
 * All endpoint references (keys, hooks, endpoint map) are derived from the
 * central definitions in `./endpoints.ts` so generated names live in one place.
 */
import { generatedApi } from './api.generated';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { ROLES_API_ENDPOINTS as cfg } from './config';

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * A single `Roles` tag type is used for both the collection and individual
 * entities. The list query provides a per-item tag for every role plus a
 * dedicated `{ type: 'Roles', id: 'LIST' }` tag, so that:
 * - updating/deleting a single role invalidates only that role's `{ id }` tag,
 *   which refetches both the affected `getRole` query and the list (the list
 *   provides that same per-item tag);
 * - creating a role invalidates `{ id: 'LIST' }` to refetch the collection
 *   without touching unrelated entities.
 *
 * This replaces the previous manual `dispatch(invalidateTags(...))` workaround in
 * `onQueryStarted`: that was attributed to redux-toolkit#5093, but that issue is
 * about the `isSuccess` flag during pessimistic updates, not tag invalidation —
 * `invalidatesTags` propagates to the list correctly via the per-item tags.
 */
export const rolesApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['Roles'],
  endpoints: {
    [cfg.createRole.generated]: {
      invalidatesTags: [{ type: 'Roles', id: LIST_TAG_ID }],
    },
    [cfg.listRoles.generated]: {
      providesTags: (result) => provideListTags(result, 'Roles'),
    },
    [cfg.getRole.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'Roles', id: arg.id }],
    },
    [cfg.updateRole.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Roles', id: arg.id }],
    },
    [cfg.deleteRole.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Roles', id: arg.id }],
    },
  },
});

// Re-export hooks with cleaner names via endpoint definitions
export const hookExports: HookExports<typeof cfg, typeof rolesApi> = createHookExports(
  cfg,
  rolesApi,
);

// Re-export endpoints object with cleaner names for direct usage
export const roleApiEndpoints = createEndpointExports(cfg, rolesApi);
